/**
 * Shared timezone-aware date utilities.
 * All display functions require a timeZone parameter — the calling hook provides the default.
 */

/** Check if two dates fall on the same calendar day in the given timezone. */
export function isSameDayInTimezone(a: Date, b: Date, timeZone: string): boolean {
    const fmt = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone,
    });
    return fmt.format(a) === fmt.format(b);
}

/** Check if a date is today in the given timezone. */
export function isTodayInTimezone(date: Date, timeZone: string): boolean {
    return isSameDayInTimezone(date, new Date(), timeZone);
}

/** Format a date for display using Intl.DateTimeFormat. */
export function formatDateTimeInTimezone(
    date: Date,
    timeZone: string,
    options: Omit<Intl.DateTimeFormatOptions, "timeZone"> = {},
): string {
    return new Intl.DateTimeFormat("en-US", { ...options, timeZone }).format(date);
}

/** Format just the time portion of a date. */
export function formatTimeInTimezone(date: Date, timeZone: string): string {
    return formatDateTimeInTimezone(date, timeZone, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
}

/** Check if a date is tomorrow in the given timezone. */
export function isTomorrowInTimezone(date: Date, timeZone: string): boolean {
    const fmt = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone,
    });
    const todayParts = fmt.format(new Date());
    const [y, m, d] = todayParts.split("-").map(Number);
    const tomorrow = new Date(y, m - 1, d + 1);
    return fmt.format(date) === fmt.format(tomorrow);
}

/** Format a short date (e.g., "Apr 7, 2026"). */
export function formatShortDateInTimezone(date: Date, timeZone: string): string {
    return formatDateTimeInTimezone(date, timeZone, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}
